import { Dispatch, MutableRefObject, SetStateAction, useCallback } from 'react';
import {
  DataTableSelectAllChangeEvent,
  DataTableSelectionMultipleChangeEvent,
  DataTableValueArray,
} from 'primereact/datatable';
import { SelectedRowsContextType } from '../../../types/context';
import { createSelectedRowsByData } from '../../../utils/createSelectedRowsByData';
import { resolveDeselectedRowIds } from '../../../utils/resolveDeselectedRowIds';
import { resolveSelectAllSelection } from '../../../utils/resolveSelectAllSelection';

type UseSelectedRowsReferenceHandlersParams = {
  selectAllReferenceChecked: boolean;
  setSelectAllReferenceChecked: Dispatch<SetStateAction<boolean>>;
  setSelectedReferenceRows: Dispatch<SetStateAction<any[]>>;
  setSelectedReferenceVirtualRows: Dispatch<SetStateAction<number>>;
  deselectedReferenceRowIdsRef: MutableRefObject<Set<string>>;
  updateDeselectedReferenceRowIds: (ids: Set<string>) => void;
};

type UseSelectedRowsReferenceHandlersResult = Pick<
  SelectedRowsContextType,
  | 'processSelectedReferenceRowsChange'
  | 'processSelectAllReferenceChange'
  | 'resetReferenceSelectedRows'
>;

// Reference grid — FullscreenReference owns its data/datasetData, so the
// handlers take them as parameters (mirrors the slave pattern).
export const useSelectedRowsReferenceHandlers = ({
  selectAllReferenceChecked,
  setSelectAllReferenceChecked,
  setSelectedReferenceRows,
  setSelectedReferenceVirtualRows,
  deselectedReferenceRowIdsRef,
  updateDeselectedReferenceRowIds,
}: UseSelectedRowsReferenceHandlersParams): UseSelectedRowsReferenceHandlersResult => {
  const processSelectedReferenceRowsChange = useCallback(
    (
      checkboxEvent: DataTableSelectionMultipleChangeEvent<DataTableValueArray>,
      referenceData: any[],
      referenceDatasetData: any,
      dataKey: string,
      selectedVirtualRows: number,
      // Exclusions are submitted as ids, so they are keyed by control.idItem —
      // which may differ from the grid's dataKey, or be a positional _id.
      exclusionIdKey: string
    ) => {
      const value: Record<string, any>[] = checkboxEvent.value;

      // Fall back to referenceData.length when totalCount header is absent
      // (small datasets without cached-query pagination metadata). In that
      // case referenceData IS the full dataset, so re-selecting every loaded
      // row must re-enable select-all.
      const totalCount =
        referenceDatasetData?.totalCount ?? referenceData?.length ?? 0;

      const isSelectAllMode: boolean =
        selectAllReferenceChecked ||
        selectedVirtualRows > 0 ||
        deselectedReferenceRowIdsRef.current.size > 0;

      if (isSelectAllMode && totalCount > 0) {
        const nextDeselectedRowIds = resolveDeselectedRowIds(
          deselectedReferenceRowIdsRef.current,
          value,
          referenceData ?? [],
          exclusionIdKey,
          true
        );
        const selection = resolveSelectAllSelection(
          referenceData ?? [],
          totalCount,
          nextDeselectedRowIds,
          exclusionIdKey
        );
        updateDeselectedReferenceRowIds(nextDeselectedRowIds);
        setSelectAllReferenceChecked(nextDeselectedRowIds.size === 0);
        setSelectedReferenceRows(selection.selectedRows);
        setSelectedReferenceVirtualRows(selection.selectedVirtualRows);
        return;
      }

      const newSelectedRows = createSelectedRowsByData(
        value,
        referenceData ?? [],
        dataKey
      );

      if (selectAllReferenceChecked) {
        setSelectAllReferenceChecked(false);
      }

      if (
        totalCount > 0 &&
        newSelectedRows.length + selectedVirtualRows === totalCount
      ) {
        setSelectedReferenceRows(value);
        setSelectAllReferenceChecked(true);
        return;
      }

      setSelectedReferenceRows(newSelectedRows);
      return;
    },
    [selectAllReferenceChecked, updateDeselectedReferenceRowIds]
  );

  const processSelectAllReferenceChange = useCallback(
    (
      checkboxEvent: DataTableSelectAllChangeEvent,
      referenceData: any[],
      referenceDatasetData: any
    ) => {
      setSelectAllReferenceChecked(checkboxEvent.checked);
      updateDeselectedReferenceRowIds(new Set<string>());

      if (checkboxEvent.checked) {
        const newSelectedRows = [...(referenceData ?? [])];
        const datasetTotalCount = referenceDatasetData?.totalCount ?? 0;

        if (newSelectedRows.length < datasetTotalCount) {
          const numberOfRowsToAdd = datasetTotalCount - newSelectedRows.length;
          setSelectedReferenceVirtualRows(numberOfRowsToAdd);
        }
        setSelectedReferenceRows(newSelectedRows);
        return;
      }

      setSelectedReferenceVirtualRows(0);
      setSelectedReferenceRows([]);
    },
    [updateDeselectedReferenceRowIds]
  );

  const resetReferenceSelectedRows = useCallback(() => {
    setSelectedReferenceRows([]);
    setSelectedReferenceVirtualRows(0);
    setSelectAllReferenceChecked(false);
    updateDeselectedReferenceRowIds(new Set<string>());
  }, [updateDeselectedReferenceRowIds]);

  return {
    processSelectedReferenceRowsChange,
    processSelectAllReferenceChange,
    resetReferenceSelectedRows,
  };
};
